import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";
import { opportunityRepo } from "@repositories";
import { useQuery } from "@tanstack/react-query";
import { type FC, useMemo } from "react";
import { EventDetails } from "../event-details/EventDetails";
import { GroupOpportunitySlots } from "../group-opportunity-slots/GroupOpportunitySlots";

/** Above stacked lead/picker dialogs (picker theme already bumps modal by ~8). */
const NESTED_MODAL_Z_OFFSET = 40;

export type OpportunityPreviewView = "details" | "group-slots";

type Props = {
  open: boolean;
  opportunityId: string | null;
  onClose: () => void;
  /** Raise above a parent dialog (summer-campaign lead flow / picker). */
  nestedInDialog?: boolean;
  /** details = EventDetails; group-slots = instance list for a parent group. */
  view?: OpportunityPreviewView;
};

export const OpportunityPreviewDialog: FC<Props> = ({
  open,
  opportunityId,
  onClose,
  nestedInDialog = false,
  view = "details",
}) => {
  const theme = useTheme();
  const nestedTheme = useMemo(() => {
    if (!nestedInDialog) return null;
    return createTheme(theme, {
      zIndex: {
        modal: theme.zIndex.modal + NESTED_MODAL_Z_OFFSET,
      },
    });
  }, [theme, nestedInDialog]);

  const { data: opportunity } = useQuery({
    queryKey: ["opportunity-preview", opportunityId],
    queryFn: async () => {
      if (!opportunityId) return undefined;
      return opportunityRepo.findOne({
        where: { id: opportunityId },
        include: {
          manager: true,
          teams: { include: { team: true } },
          requiredSkills: true,
          progressStatus: true,
          partnerOrganization: true,
          associationRequested: {
            include: {
              user: true,
            },
          },
        },
      });
    },
    enabled: open && !!opportunityId,
  });

  const title =
    view === "group-slots"
      ? `מופעים - ${opportunity?.title ?? ""}`
      : (opportunity?.title ?? "");

  const dialog = (
    <Dialog
      open={open}
      onClose={onClose}
      className="event-details-dialog"
      maxWidth={view === "group-slots" ? "md" : "sm"}
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {view === "group-slots" && opportunityId ? (
          <GroupOpportunitySlots
            parentId={opportunityId}
            parentTitle={opportunity?.title}
            variant="sidebar"
            readOnly
            disableInstanceNavigation
          />
        ) : (
          <EventDetails eventDetails={opportunity} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>סגור</Button>
      </DialogActions>
    </Dialog>
  );

  if (!nestedTheme) return dialog;
  return <ThemeProvider theme={nestedTheme}>{dialog}</ThemeProvider>;
};
